/* eslint-disable react/forbid-prop-types */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Container, Box, Typography, FormControlLabel, Radio, Card, RadioGroup, Divider,
} from "@mui/material";
import { H5 } from "components/CustomTypography";
import TextFieldComponent from "components/TextField";
import ButtonComponent from "components/Button";
import {
  schema, getTextfieldData, getTextfieldData2, getButtonData,
} from "./helper";

const SellerDeliveryServiceEditor = ({
  setState, service, updateServiceCall, cancelEdit,
}) => {
  const { shippingoption, orderminimum, shipping, delivery_accountid } = service;
  
  // Check if Shopana delivery is enabled for this shop
  const isShopanaDeliveryEnabled = !!delivery_accountid;
  
  // List of Shopana delivery options
  const shopanaDeliveryValues = ["thirdparty", "thirdpartyfree", "thirdpartyminimum"];
  
  // Reset shipping option if Shopana delivery is selected but not enabled
  useEffect(() => {
    if (!isShopanaDeliveryEnabled && shopanaDeliveryValues.includes(shippingoption)) {
      setState("nodelivery", "shippingoption");
    }
  }, [isShopanaDeliveryEnabled, shippingoption]);

  const [blur, setblur] = React.useState({
    shippingoption: false,
    orderminimum: false,
    shipping: false,
  });

  const onFinish = () => {
    updateServiceCall({
      shippingoption, orderminimum, shipping,
    });
  };

  const handleTextFieldChange = (event, field) => {
    setState(event.target.value, field);
    setblur({ ...blur, [field]: true });
  };

  const handleRadioButtonChange = (event, field) => {
    setState(event.target.value, field);
    setblur({ ...blur, [field]: true });
  };

  const hasError = () => {
    try {
      return !schema.validateSync(service);
    } catch (err) {
      return true;
    }
  };

  const handleBlur = (field) => {
    setblur({ ...blur, [field]: true });
  };

  const isTouched = () => (blur.shippingoption || blur.orderminimum || blur.shipping);

  const getError = (field) => {
    if (blur[field] && hasError()) {
      try {
        schema.validateSyncAt(field, service);
      } catch (err) {
        return err.message;
      }
    }
    return null;
  };

  // Group delivery options by type
  const shopDeliveryOptions = [
    { label: "Standard delivery charges (Added on accepting order)", value: "actualcost" },
    { label: "FREE on all orders", value: "freeshipping" },
    { label: "FREE on orders above minimum (Added at checkout)", value: "shippingcost" },
  ];

  const shopanaDeliveryOptions = [
    { label: "Standard delivery charges (Added at checkout)", value: "thirdparty" },
    { label: "FREE on all orders", value: "thirdpartyfree" },
    { label: "FREE on orders above minimum (Added at checkout)", value: "thirdpartyminimum" },
  ];

  return (
    <Container>
      <Box>
        <Card sx={{ p: 3 }}>
          {/* No Delivery Option */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              value="nodelivery"
              control={
                <Radio
                  checked={shippingoption === "nodelivery"}
                  onChange={(event) => handleRadioButtonChange(event, "shippingoption")}
                />
              }
              label={
                <Typography sx={{ fontWeight: shippingoption === "nodelivery" ? 600 : 400 }}>
                  No Delivery (Disabled)
                </Typography>
              }
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Delivery by Shop Section */}
          <Box sx={{ mb: 3 }}>
            <H5 sx={{ mb: 2, color: "primary.main" }}>
              Delivery by Shop
            </H5>
            <RadioGroup
              value={shippingoption}
              onChange={(event) => handleRadioButtonChange(event, "shippingoption")}
            >
              {shopDeliveryOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontWeight: shippingoption === option.value ? 600 : 400 }}>
                      {option.label}
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
              ))}
            </RadioGroup>

            {/* Additional fields for shippingcost option */}
            {shippingoption === "shippingcost" && (
              <Box sx={{ ml: 4, mt: 2 }}>
                {getTextfieldData(orderminimum, shipping).map((element) => (
                  <Box key={element.onChangeField} sx={{ mb: 2 }}>
                    <TextFieldComponent
                      required
                      label={element.label}
                      value={element.value}
                      error={!!getError(element.onChangeField)}
                      helperText={getError(element.onChangeField)}
                      onChange={(event) => handleTextFieldChange(event, element.onChangeField)}
                      fullWidth
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Delivery via Shopana Delivery Partner Section */}
          <Box>
            <H5 sx={{ mb: 2, color: isShopanaDeliveryEnabled ? "primary.main" : "text.disabled" }}>
              Delivery via Shopana Delivery Partner
            </H5>
            
            {isShopanaDeliveryEnabled ? (
              <>
                <RadioGroup
                  value={shippingoption}
                  onChange={(event) => handleRadioButtonChange(event, "shippingoption")}
                >
                  {shopanaDeliveryOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <Typography sx={{ fontWeight: shippingoption === option.value ? 600 : 400 }}>
                          {option.label}
                        </Typography>
                      }
                      sx={{ mb: 1 }}
                    />
                  ))}
                </RadioGroup>

                {/* Additional fields for thirdpartyminimum option */}
                {shippingoption === "thirdpartyminimum" && (
                  <Box sx={{ ml: 4, mt: 2 }}>
                    {getTextfieldData2(orderminimum).map((element) => (
                      <Box key={element.onChangeField} sx={{ mb: 2 }}>
                        <TextFieldComponent
                          required
                          label={element.label}
                          value={element.value}
                          error={!!getError(element.onChangeField)}
                          helperText={getError(element.onChangeField)}
                          onChange={(event) => handleTextFieldChange(event, element.onChangeField)}
                          fullWidth
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  p: 3,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  border: "1px dashed",
                  borderColor: "grey.400",
                }}
              >
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  Shopana Delivery Partner service is not enabled for your shop.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contact support to enable third-party delivery services for your shop.
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>
      <br />
      <Typography align="center">
        {getButtonData(onFinish, cancelEdit, hasError, isTouched).map((element) => (
          <ButtonComponent
            key={element.name}
            sx={{
              m: 2,
            }}
            type={element.type}
            onClick={element.onClick}
            disabled={element.disabled}
            name={element.name}
          />
        ))}
      </Typography>
    </Container>
  );
};

SellerDeliveryServiceEditor.propTypes = {
  setState: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
  updateService: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
};

export default SellerDeliveryServiceEditor;
